import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  AlertTriangle,
  Building2,
  CalendarDays,
  ClipboardList,
  Clock,
  Columns3,
  Download,
  FileSpreadsheet,
  FileText,
  Filter,
  History,
  Inbox,
  Loader2,
  PieChart,
  Play,
  Printer,
  RefreshCw,
  RotateCcw,
  Search,
  TrendingUp,
  Users,
} from "lucide-react";
import API from "../../services/api";
import { AuthContext } from "../Context/AuthContext";
import { useToast } from "../Context/ToastContext";
import { printRowsAsTable } from "./reportPrint";
import { useEmployees } from "../../hooks/useEmployees";
import SearchableSelect from "../../components/common/SearchableSelect";
import { employeeSearchSelectOptions } from "../../components/common/employeeUtils";
import { fetchLeaveTypes } from "../leave/leaveApi";
import { useLeaveOrg } from "../../components/pages/Leave/useLeaveOrg";
import { exportRowsToXlsx, toIsoDate } from "./employeeReportsUtils";
import { exportRowsToPdf } from "./attendanceReportsUtils";
import {
  buildLeaveBalanceReportRows,
  buildLeaveRegisterRows,
  buildLeaveUtilizationRows,
  buildPendingLeaveRows,
  getLeaveBalanceReportColumns,
} from "./leaveReportsUtils";
import ReportsEnterpriseShell from "./components/ReportsEnterpriseShell";
import { useReportsNavigation } from "./useReportsNavigation";
import { PAYROLL_HUB_REPORTS } from "./PayrollReportsWorkspace";
import { EXTERNAL_REPORT_NAV } from "./reportsNavConfig";
import "../../styles/payrollReportsWorkspace.css";
import "../../styles/leaveReports.css";

const PAGE_SIZE = 50;

const REGISTER_COLS = ["Employee", "Leave Type", "From Date", "To Date", "Status"];
const UTILIZATION_COLS = ["Department", "Leaves Availed", "Leaves Remaining"];
const PENDING_COLS = ["Employee", "Leave Type", "From Date", "Days", "Status"];

const VALID_LEAVE_KEYS = new Set([
  "leave_balance",
  "leave_register",
  "leave_utilization",
  "leave_pending",
]);

const APPROVER_OPTIONS = [
  { value: "", label: "All Approvers" },
  { value: "manager", label: "Reporting Manager" },
  { value: "hr", label: "HR Approver" },
];

const LEAVE_REPORT_META = {
  leave_balance: {
    icon: PieChart,
    title: "Leave Balance Report",
    description: "Current leave balance of employees.",
  },
  leave_register: {
    icon: ClipboardList,
    title: "Leave Register",
    description: "All leave transactions for the selected period.",
  },
  leave_utilization: {
    icon: TrendingUp,
    title: "Leave Utilization Report",
    description: "Leave availed versus remaining balance by department.",
  },
  leave_pending: {
    icon: Clock,
    title: "Pending Leave Requests",
    description: "Pending leave approval requests awaiting action.",
  },
};

function rowsToColumns(rows, preferred = []) {
  if (preferred.length) return preferred.map((key) => ({ key, num: false }));
  if (!rows.length) return [];
  return Object.keys(rows[0]).map((key) => ({ key, num: typeof rows[0][key] === "number" }));
}

function downloadCsv(rows, filename) {
  if (!rows.length) return;
  const cols = Object.keys(rows[0]);
  const escape = (v) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [cols.join(","), ...rows.map((r) => cols.map((c) => escape(r[c])).join(","))];
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function LeaveReportsPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, role } = useContext(AuthContext);
  const generatedBy = user?.name || user?.full_name || user?.email || role || "—";
  const [theme, setTheme] = useState(() => localStorage.getItem("prw:theme") || "light");
  const nav = useReportsNavigation({ payrollReports: PAYROLL_HUB_REPORTS });
  const { orgId } = useLeaveOrg();
  const { employees: employeeOptions, loading: employeesLoading } = useEmployees();
  const printRef = useRef(null);
  const colMenuRef = useRef(null);

  const currentYear = useMemo(() => new Date().getFullYear(), []);
  const today = useMemo(() => toIsoDate(new Date()), []);
  const monthStart = useMemo(() => {
    const d = new Date();
    return toIsoDate(new Date(d.getFullYear(), d.getMonth(), 1));
  }, []);

  const selectedKey = useMemo(() => {
    const key = searchParams.get("report");
    return VALID_LEAVE_KEYS.has(key) ? key : "leave_balance";
  }, [searchParams]);

  const selectedMeta = LEAVE_REPORT_META[selectedKey];
  const selectedNavItem =
    EXTERNAL_REPORT_NAV.find((n) => n.key === selectedKey) ||
    EXTERNAL_REPORT_NAV.find((n) => n.key === "leave_balance");
  const ReportIcon = selectedMeta?.icon || CalendarDays;

  useEffect(() => {
    const key = searchParams.get("report");
    if (!VALID_LEAVE_KEYS.has(key)) {
      navigate("/reports/leave?report=leave_balance", { replace: true });
    }
  }, [searchParams, navigate]);

  useEffect(() => {
    localStorage.setItem("prw:theme", theme);
  }, [theme]);

  const [departments, setDepartments] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [gridSearch, setGridSearch] = useState("");
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState({ col: null, dir: "asc" });
  const [density, setDensity] = useState("comfortable");
  const [visibleCols, setVisibleCols] = useState(null);
  const [showColMenu, setShowColMenu] = useState(false);

  const [balanceFilters, setBalanceFilters] = useState({
    departmentId: "",
    leaveTypeId: "",
  });
  const [registerFilters, setRegisterFilters] = useState({
    employeeId: "",
    fromDate: monthStart,
    toDate: today,
    leaveTypeId: "",
  });
  const [utilizationFilters, setUtilizationFilters] = useState({
    year: currentYear,
    departmentId: "",
  });
  const [pendingFilters, setPendingFilters] = useState({
    departmentId: "",
    approverRole: "",
  });

  const yearOptions = useMemo(() => {
    const years = [];
    for (let y = currentYear - 2; y <= currentYear + 1; y += 1) years.push(y);
    return years;
  }, [currentYear]);

  const balanceCols = useMemo(
    () => getLeaveBalanceReportColumns(balanceFilters.leaveTypeId, leaveTypes),
    [balanceFilters.leaveTypeId, leaveTypes]
  );

  const registerEmployeeOptions = useMemo(
    () => employeeSearchSelectOptions(employeeOptions),
    [employeeOptions]
  );

  useEffect(() => {
    API.get("/setup/departments")
      .then((r) => setDepartments(r.data || []))
      .catch(() => setDepartments([]));
  }, []);

  useEffect(() => {
    if (!orgId) {
      setLeaveTypes([]);
      return;
    }
    fetchLeaveTypes(orgId)
      .then((types) => {
        const list = Array.isArray(types) ? types : [];
        setLeaveTypes(list.filter((t) => t.is_active !== false && t.is_archived !== true));
      })
      .catch(() => setLeaveTypes([]));
  }, [orgId]);

  // Reset grid when switching leave report
  useEffect(() => {
    setRows([]);
    setHasGenerated(false);
    setGeneratedAt(null);
    setError(null);
    setGridSearch("");
    setPage(1);
    setSort({ col: null, dir: "asc" });
    setVisibleCols(null);
  }, [selectedKey]);

  useEffect(() => {
    const onDoc = (e) => {
      if (colMenuRef.current && !colMenuRef.current.contains(e.target)) setShowColMenu(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const loadRows = useCallback(async () => {
    if (selectedKey === "leave_balance") {
      return buildLeaveBalanceReportRows({
        orgId,
        year: currentYear,
        departmentId: balanceFilters.departmentId,
        leaveTypeId: balanceFilters.leaveTypeId,
        employees: employeeOptions,
        leaveTypes,
      });
    }
    if (selectedKey === "leave_register") {
      return buildLeaveRegisterRows({
        orgId,
        employeeId: registerFilters.employeeId,
        fromDate: registerFilters.fromDate,
        toDate: registerFilters.toDate,
        leaveTypeId: registerFilters.leaveTypeId,
        employees: employeeOptions,
      });
    }
    if (selectedKey === "leave_utilization") {
      return buildLeaveUtilizationRows({
        orgId,
        year: utilizationFilters.year,
        departmentId: utilizationFilters.departmentId,
        employees: employeeOptions,
      });
    }
    return buildPendingLeaveRows({
      orgId,
      departmentId: pendingFilters.departmentId,
      approverRole: pendingFilters.approverRole,
      employees: employeeOptions,
    });
  }, [
    selectedKey,
    orgId,
    currentYear,
    balanceFilters,
    registerFilters,
    utilizationFilters,
    pendingFilters,
    employeeOptions,
    leaveTypes,
  ]);

  const preferredColumns = useMemo(() => {
    if (selectedKey === "leave_balance") return balanceCols;
    if (selectedKey === "leave_register") return REGISTER_COLS;
    if (selectedKey === "leave_utilization") return UTILIZATION_COLS;
    if (selectedKey === "leave_pending") return PENDING_COLS;
    return [];
  }, [selectedKey, balanceCols]);

  const columns = useMemo(() => rowsToColumns(rows, preferredColumns), [rows, preferredColumns]);

  const activeColumns = useMemo(
    () => columns.filter((c) => (visibleCols ? visibleCols[c.key] !== false : true)),
    [columns, visibleCols]
  );

  const filteredRows = useMemo(() => {
    const q = gridSearch.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      activeColumns.some((c) => String(r[c.key] ?? "").toLowerCase().includes(q))
    );
  }, [rows, gridSearch, activeColumns]);

  const sortedRows = useMemo(() => {
    if (!sort.col) return filteredRows;
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...filteredRows].sort((a, b) => {
      const av = a[sort.col];
      const bv = b[sort.col];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
  }, [filteredRows, sort]);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / PAGE_SIZE));
  const pageRows = useMemo(
    () => sortedRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [sortedRows, page]
  );

  const runReport = async () => {
    if (!orgId) {
      toast.error("Organisation context is not available.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await loadRows();
      setRows(Array.isArray(data) ? data : []);
      setHasGenerated(true);
      setGeneratedAt(new Date());
      setPage(1);
      } catch {
      setError("Failed to generate report. Please try again.");
      setRows([]);
      toast.error("Failed to generate report. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const resetFilters = () => {
    setBalanceFilters({ departmentId: "", leaveTypeId: "" });
    setRegisterFilters({
      employeeId: "",
      fromDate: monthStart,
      toDate: today,
      leaveTypeId: "",
    });
    setUtilizationFilters({ year: currentYear, departmentId: "" });
    setPendingFilters({ departmentId: "", approverRole: "" });
    setGridSearch("");
    setSort({ col: null, dir: "asc" });
    setPage(1);
  };

  const exportData = (fmt) => {
    if (!rows.length) {
      toast.error("Generate the report before exporting.");
      return;
    }
    const name = selectedMeta?.title || "Leave Report";
    try {
      if (fmt === "pdf") {
        exportRowsToPdf(rows, name, `${selectedKey}.pdf`);
        toast.success("PDF export downloaded.");
        return;
      }
      if (fmt === "csv") {
        downloadCsv(rows, `${selectedKey}.csv`);
        toast.success("CSV export downloaded.");
        return;
      }
      exportRowsToXlsx(rows, name, `${selectedKey}.xlsx`);
      toast.success("Excel export downloaded.");
    } catch {
      toast.error("Export failed. Please try again.");
    }
  };

  const printReport = () => {
    if (!sortedRows.length) {
      toast.error("Generate the report before printing.");
      return;
    }
    const period =
      selectedKey === "leave_register"
        ? `${registerFilters.fromDate} \u2192 ${registerFilters.toDate}`
        : selectedKey === "leave_utilization"
          ? utilizationFilters.year
          : currentYear;
    printRowsAsTable({
      title: selectedMeta?.title || "Leave Report",
      subtitle: [`Period: ${period}`, `Rows: ${sortedRows.length}`, `Generated by: ${generatedBy}`].join("  |  "),
      columns: activeColumns.map((c) => c.key),
      rows: sortedRows,
    });
  };

  const toggleSort = (col) => {
    setSort((prev) =>
      prev.col === col
        ? { col, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { col, dir: "asc" }
    );
  };

  const toggleColumn = (key) => {
    setVisibleCols((prev) => {
      const base = prev || Object.fromEntries(columns.map((c) => [c.key, true]));
      return { ...base, [key]: !base[key] };
    });
  };


  const showEmptyPrompt = !loading && !hasGenerated && !rows.length;

  const renderFilters = () => {
    if (selectedKey === "leave_balance") {
      return (
        <>
          <div className="prw__filter">
            <label htmlFor="lr-dept">
              <Building2 size={12} /> Department
            </label>
            <select
              id="lr-dept"
              value={balanceFilters.departmentId}
              onChange={(e) => setBalanceFilters((f) => ({ ...f, departmentId: e.target.value }))}
            >
              <option value="">All Departments</option>
              {departments.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>
          <div className="prw__filter">
            <label htmlFor="lr-type">
              <ClipboardList size={12} /> Leave Type
            </label>
            <select
              id="lr-type"
              value={balanceFilters.leaveTypeId}
              onChange={(e) => setBalanceFilters((f) => ({ ...f, leaveTypeId: e.target.value }))}
            >
              <option value="">All Leave Types</option>
              {leaveTypes.map((t) => (
                <option key={t.leave_type_id} value={t.leave_type_id}>
                  {t.name || t.code}
                </option>
              ))}
            </select>
          </div>
        </>
      );
    }

    if (selectedKey === "leave_register") {
      return (
        <>
          <div className="prw__filter prw__filter--search">
            <SearchableSelect
              label="Employee"
              value={registerFilters.employeeId}
              options={registerEmployeeOptions}
              loading={employeesLoading}
              allowEmpty
              emptyLabel="All Employees"
              searchPlaceholder="Search by name or employee code…"
              noResultsText="No employees match your search"
              onChange={(id) => setRegisterFilters((f) => ({ ...f, employeeId: id || "" }))}
            />
          </div>
          <div className="prw__filter">
            <label htmlFor="lr-from">
              <CalendarDays size={12} /> From Date
            </label>
            <input
              id="lr-from"
              type="date"
              value={registerFilters.fromDate}
              onChange={(e) =>
                setRegisterFilters((f) => ({ ...f, fromDate: e.target.value || f.fromDate }))
              }
            />
          </div>
          <div className="prw__filter">
            <label htmlFor="lr-to">
              <CalendarDays size={12} /> To Date
            </label>
            <input
              id="lr-to"
              type="date"
              value={registerFilters.toDate}
              onChange={(e) =>
                setRegisterFilters((f) => ({ ...f, toDate: e.target.value || f.toDate }))
              }
            />
          </div>
          <div className="prw__filter">
            <label htmlFor="lr-reg-type">
              <ClipboardList size={12} /> Leave Type
            </label>
            <select
              id="lr-reg-type"
              value={registerFilters.leaveTypeId}
              onChange={(e) => setRegisterFilters((f) => ({ ...f, leaveTypeId: e.target.value }))}
            >
              <option value="">All Leave Types</option>
              {leaveTypes.map((t) => (
                <option key={t.leave_type_id} value={t.leave_type_id}>
                  {t.name || t.code}
                </option>
              ))}
            </select>
          </div>
        </>
      );
    }

    if (selectedKey === "leave_utilization") {
      return (
        <>
          <div className="prw__filter">
            <label htmlFor="lr-year">
              <CalendarDays size={12} /> Year
            </label>
            <select
              id="lr-year"
              value={utilizationFilters.year}
              onChange={(e) =>
                setUtilizationFilters((f) => ({ ...f, year: Number(e.target.value) }))
              }
            >
              {yearOptions.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
          <div className="prw__filter">
            <label htmlFor="lr-util-dept">
              <Building2 size={12} /> Department
            </label>
            <select
              id="lr-util-dept"
              value={utilizationFilters.departmentId}
              onChange={(e) =>
                setUtilizationFilters((f) => ({ ...f, departmentId: e.target.value }))
              }
            >
              <option value="">All Departments</option>
              {departments.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>
        </>
      );
    }

    return (
      <>
        <div className="prw__filter">
          <label htmlFor="lr-pending-dept">
            <Building2 size={12} /> Department
          </label>
          <select
            id="lr-pending-dept"
            value={pendingFilters.departmentId}
            onChange={(e) => setPendingFilters((f) => ({ ...f, departmentId: e.target.value }))}
          >
            <option value="">All Departments</option>
            {departments.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        </div>
        <div className="prw__filter">
          <label htmlFor="lr-approver">
            <Users size={12} /> Approver
          </label>
          <select
            id="lr-approver"
            value={pendingFilters.approverRole}
            onChange={(e) => setPendingFilters((f) => ({ ...f, approverRole: e.target.value }))}
          >
            {APPROVER_OPTIONS.map((opt) => (
              <option key={opt.value || "all"} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </>
    );
  };

  return (
    <div className="prw lr-page" ref={printRef} data-theme={theme}>
      <ReportsEnterpriseShell
        categoryOrder={nav.categoryOrder}
        reportsByCategory={nav.reportsByCategory}
        selectedKey={selectedKey}
        selectedReport={nav.selectedReport || selectedNavItem}
        reportSearch={nav.reportSearch}
        onReportSearchChange={nav.onReportSearchChange}
        favorites={nav.favorites}
        onToggleFavorite={nav.onToggleFavorite}
        recentReports={nav.recentReports}
        favReports={nav.favReports}
        onSelectReport={nav.onSelectReport}
        onThemeChange={setTheme}
        theme={theme}
        moduleTitle={nav.moduleTitle}
      >
        <div className="prw__body">
          <div className="prw__standard">
            <motion.div
              className="prw__page-header"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="prw__page-header-main">
                <div className="prw__page-icon">
                  <ReportIcon size={26} />
                </div>
                <div>
                  <h1 className="prw__page-title">{selectedMeta?.title || "Leave Reports"}</h1>
                  <p className="prw__page-desc">{selectedMeta?.description}</p>
                  <div className="prw__page-meta">
                    <span className="prw__meta-chip">
                      <CalendarDays size={13} />
                      Period:{" "}
                      <b>
                        {selectedKey === "leave_register"
                          ? `${registerFilters.fromDate} → ${registerFilters.toDate}`
                          : selectedKey === "leave_utilization"
                            ? utilizationFilters.year
                            : currentYear}
                      </b>
                    </span>
                    <span className="prw__meta-chip">
                      <Users size={13} />
                      Generated by: <b>{generatedBy}</b>
                    </span>
                    <span className="prw__meta-chip">
                      <History size={13} />
                      Last generated:{" "}
                      <b>{generatedAt ? generatedAt.toLocaleString() : "—"}</b>
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              className="prw__filter-card"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, delay: 0.05 }}
            >
              <p className="prw__filter-card-title">
                <Filter size={16} /> Report Filters
              </p>
              <div className="prw__filter-grid">
                {renderFilters()}
                <div className="prw__filter-actions">
                  <button
                    type="button"
                    className="prw__btn prw__btn--generate"
                    onClick={runReport}
                    disabled={loading}
                    aria-busy={loading}
                  >
                    {loading ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} />}
                    Generate
                  </button>
                  <button type="button" className="prw__btn prw__btn--ghost" onClick={resetFilters}>
                    <RotateCcw size={16} /> Reset
                  </button>
                </div>
              </div>
            </motion.div>

            <div className="prw__toolbar">
              <button
                type="button"
                className="prw__btn prw__btn--refresh prw__btn--sm"
                onClick={runReport}
                disabled={loading}
              >
                <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
              </button>
              <button
                type="button"
                className="prw__btn prw__btn--excel prw__btn--sm"
                onClick={() => exportData("xlsx")}
              >
                <FileSpreadsheet size={14} /> Excel
              </button>
              <button
                type="button"
                className="prw__btn prw__btn--pdf prw__btn--sm"
                onClick={() => exportData("pdf")}
              >
                <FileText size={14} /> PDF
              </button>
              <button
                type="button"
                className="prw__btn prw__btn--csv prw__btn--sm"
                onClick={() => exportData("csv")}
              >
                <Download size={14} /> CSV
              </button>
              <button
                type="button"
                className="prw__btn prw__btn--print prw__btn--sm"
                onClick={printReport}
              >
                <Printer size={14} /> Print
              </button>

              <div className="prw__toolbar-right">
                <select
                  className="prw__density-select"
                  value={density}
                  onChange={(e) => setDensity(e.target.value)}
                  aria-label="Table density"
                >
                  <option value="compact">Compact</option>
                  <option value="comfortable">Comfortable</option>
                  <option value="spacious">Spacious</option>
                </select>
                <div style={{ position: "relative" }} ref={colMenuRef}>
                  <button
                    type="button"
                    className="prw__btn prw__btn--ghost prw__btn--sm"
                    onClick={() => setShowColMenu((v) => !v)}
                    aria-expanded={showColMenu}
                  >
                    <Columns3 size={14} /> Columns
                  </button>
                  {showColMenu && columns.length > 0 && (
                    <div className="prw__col-menu">
                      {columns.map((c) => (
                        <label key={c.key}>
                          <input
                            type="checkbox"
                            checked={visibleCols ? visibleCols[c.key] !== false : true}
                            onChange={() => toggleColumn(c.key)}
                          />
                          {c.key}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                <div className="prw__grid-search">
                  <Search size={14} className="prw__grid-search-icon" />
                  <input
                    placeholder="Search rows…"
                    value={gridSearch}
                    onChange={(e) => {
                      setGridSearch(e.target.value);
                      setPage(1);
                    }}
                    aria-label="Search report rows"
                  />
                </div>
              </div>
            </div>

            {error && (
              <div className="prw__error">
                <AlertTriangle size={16} /> {error}
              </div>
            )}

            <div className="prw__content">
              <div className="prw__report">
                {showEmptyPrompt && (
                  <motion.div
                    className="prw__setup-banner"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <div className="prw__setup-banner-icon">
                      <ReportIcon size={28} />
                    </div>
                    <div>
                      <h3 className="prw__setup-banner-title">
                        Configure filters to generate {selectedMeta?.title}
                      </h3>
                      <p className="prw__setup-banner-desc">
                        Choose filters above, then click <strong>Generate</strong> to preview results.
                      </p>
                    </div>
                  </motion.div>
                )}

                {loading ? (
                  <div className="prw__skeleton" aria-busy="true" aria-label="Generating report">
                    <div className="prw__skeleton-row" />
                    <div className="prw__skeleton-row" />
                    <div className="prw__skeleton-row" />
                    <div className="prw__skeleton-row" />
                    <div className="prw__skeleton-row" />
                  </div>
                ) : hasGenerated && !rows.length ? (
                  <motion.div
                    className="prw__empty"
                    initial={{ opacity: 0, scale: 0.98 }}
                    animate={{ opacity: 1, scale: 1 }}
                  >
                    <div className="prw__empty-illus">
                      <Inbox size={36} />
                    </div>
                    <h3>No records found</h3>
                    <p>
                      No data matches your current filter selection. Try adjusting the filters and
                      regenerate.
                    </p>
                  </motion.div>
                ) : rows.length ? (
                  <motion.div
                    className={`prw__grid-wrap prw__grid-wrap--${density}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    <table className="prw__grid" role="grid" aria-label={selectedMeta?.title}>
                      <thead>
                        <tr>
                          {activeColumns.map((c) => (
                            <th
                              key={c.key}
                              className={c.num ? "num" : ""}
                              onClick={() => toggleSort(c.key)}
                              title="Click to sort"
                            >
                              {c.key}
                              <span className="prw__sort">
                                {sort.col === c.key ? (sort.dir === "asc" ? "▲" : "▼") : "⇅"}
                              </span>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {pageRows.map((r, i) => (
                          <tr key={i}>
                            {activeColumns.map((c) => (
                              <td key={c.key} className={c.num ? "num" : ""}>
                                {r[c.key] ?? "—"}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div className="prw__pager">
                      <span>
                        {sortedRows.length} rows total
                      </span>
                      {totalPages > 1 && (
                        <div className="prw__pager-btns">
                          <button
                            type="button"
                            disabled={page <= 1}
                            onClick={() => setPage((p) => p - 1)}
                          >
                            Previous
                          </button>
                          <span>
                            Page {page} of {totalPages}
                          </span>
                          <button
                            type="button"
                            disabled={page >= totalPages}
                            onClick={() => setPage((p) => p + 1)}
                          >
                            Next
                          </button>
                        </div>
                      )}
                    </div>
                  </motion.div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </ReportsEnterpriseShell>
    </div>
  );
}
